import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export type Tone = "slate" | "brand" | "green" | "amber" | "red" | "violet";

const tones: Record<Tone, string> = {
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
  brand: "bg-brand-50 text-brand-700 ring-brand-100",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  red: "bg-rose-50 text-rose-700 ring-rose-100",
  violet: "bg-accent-50 text-accent-700 ring-accent-100",
};

export function Badge({
  tone = "slate",
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const statusMap: Record<string, { tone: Tone; label: string; dot: string }> = {
  healthy: { tone: "green", label: "Healthy", dot: "bg-emerald-500" },
  error: { tone: "red", label: "Error", dot: "bg-rose-500" },
  untested: { tone: "amber", label: "Untested", dot: "bg-amber-500" },
  pending: { tone: "brand", label: "Pending", dot: "bg-brand-500" },
  paused: { tone: "slate", label: "Paused", dot: "bg-slate-400" },
  installed: { tone: "violet", label: "Needs setup", dot: "bg-accent-500" },
  configured: { tone: "green", label: "Configured", dot: "bg-emerald-500" },
};

export function StatusBadge({ status }: { status: string }) {
  const s = statusMap[status] ?? { tone: "slate" as Tone, label: status, dot: "bg-slate-400" };
  return (
    <Badge tone={s.tone}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
      {s.label}
    </Badge>
  );
}
